import React, { useState, useEffect } from "react";
import Dialog from "../../dialog/Dialog";
import Toster from "../../common/Toster";
import Pagination from "../../common/Pagination";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../../common/AdminPageLayout";
import { ADMIN_PAGE_META } from "../../common/adminPageMeta";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableCell,
  AdminTableFooter,
  AdminTableToolbar,
  TableSearchInput,
  TableEmptyRow,
  EditActionButton,
  DeleteActionButton,
  DragHandleButton,
  TableActionsCell,
  StatusBadge,
  getStatusVariant,
} from "../../common/adminTable";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as mealService from "./service";
import useTableSearch from "../../../hooks/useTableSearch";
import FilePreview from "../../FilePreview/FilePreview";

const MealIconThumbnail = ({ meal }) => {
  const [hasError, setHasError] = React.useState(false);

  if (!meal?.icon || !meal?.icon_url || hasError) {
    return (
      <div
        className="flex h-7 w-14 items-center justify-center rounded-md border border-dashed border-gray-200 bg-gray-50 text-[10px] font-medium text-gray-400"
        title="No image"
      >
        No image
      </div>
    );
  }

  return (
    <img
      src={meal.icon_url}
      alt={`${meal.name} icon`}
      className="h-7 w-7 object-cover rounded-md"
      onError={() => setHasError(true)}
    />
  );
};

// Sortable row component
const SortableRow = ({
  meal,
  index,
  moveRow,
  handleEditClick,
  handleDeleteClick,
  handleDragEnd,
}) => {
  const ref = React.useRef(null);

  const [{ isDragging }, drag] = useDrag({
    type: "row",
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (item, monitor) => {
      if (monitor.didDrop()) {
        handleDragEnd();
      }
    },
  });

  const [, drop] = useDrop({
    accept: "row",
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveRow(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  drag(drop(ref));

  return (
    <tr
      ref={ref}
      style={{ opacity: isDragging ? 0.5 : 1 }}
      className={`hover:bg-gray-50 ${
        isDragging ? "bg-gray-100 shadow-lg" : ""
      }`}
    >
      <AdminTableCell>{index + 1}</AdminTableCell>
      <AdminTableCell>
        <MealIconThumbnail meal={meal} />
      </AdminTableCell>
      <AdminTableCell>{meal.name}</AdminTableCell>
      <AdminTableCell>
        <StatusBadge variant={getStatusVariant(meal.status)}>
          {meal.status === 1 ? "Active" : "In-Active"}
        </StatusBadge>
      </AdminTableCell>
      <TableActionsCell>
        <EditActionButton
          showLabel={false}
          onClick={() => handleEditClick(meal.id)}
        />
        <DeleteActionButton
          showLabel={false}
          onClick={() => handleDeleteClick(meal)}
        />
        <DragHandleButton isDragging={isDragging} />
      </TableActionsCell>
    </tr>
  );
};

const Meals = () => {
  const queryClient = useQueryClient();

  // State management
  const [selectedMeal, setSelectedMeal] = useState(null);
  const { searchQuery, setSearchQuery, activeSearch } = useTableSearch();
  const [mealName, setMealName] = useState("");
  const [mealStatus, setMealStatus] = useState(1);
  const [iconImage, setIconImage] = useState(null);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [orderedMeals, setOrderedMeals] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [errors, setErrors] = useState({
    name: "",
  });

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearch]);

  // Queries
  const { data: mealData } = useQuery({
    queryKey: ["meals", currentPage, activeSearch],
    queryFn: () =>
      mealService.list({ page: currentPage, search: activeSearch || undefined }),
    keepPreviousData: true,
  });

  // Update orderedMeals when meals change
  useEffect(() => {
    if (mealData?.data?.data) {
      setOrderedMeals(mealData.data.data);
    }
  }, [mealData]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (formData) => mealService.create(formData),
    onSuccess: (success) => {
      setIsLoading(false);
      showSuccessMessage(
        success.response?.data?.message || "Meal added successfully!"
      );
      setAddDialogOpen(false);
      resetForm();
      queryClient.invalidateQueries(["meals"]);
    },
    onError: (error) => {
      setIsLoading(false);
      showErrorMessage(error.response?.data?.message || "Failed to add meal");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, formData }) =>
      mealService.update({ id, mealData: formData }),
    onSuccess: (success) => {
      setIsLoading(false);
      showSuccessMessage(
        success.response?.data?.message || "Meal updated successfully!"
      );
      setEditDialogOpen(false);
      resetForm();
      queryClient.invalidateQueries(["meals"]);
    },
    onError: (error) => {
      setIsLoading(false);
      showErrorMessage(
        error.response?.data?.message || "Failed to update meal"
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: mealService.deleteMeal,
    onSuccess: (success) => {
      setIsLoading(false);
      showSuccessMessage(
        success.response?.data?.message || "Meal deleted successfully!"
      );
      setDeleteDialogOpen(false);
      queryClient.invalidateQueries(["meals"]);
    },
    onError: (error) => {
      setIsLoading(false);
      setErrorMessage(error.response?.data?.message || "Failed to delete meal");
    },
  });

  const getByIdMutation = useMutation({
    mutationFn: mealService.getById,
    onSuccess: (data) => {
      setSelectedMeal(data.data);
      setMealName(data.data.name);
      setMealStatus(data.data.status);
      setIconImage(null);
      setEditDialogOpen(true);
    },
    onError: (error) => {
      setIsLoading(false);
      showErrorMessage(
        error.response?.data?.message || "Failed to fetch meal details"
      );
    },
  });

  const updateOrderMutation = useMutation({
    mutationFn: mealService.updateOrder,
    onSuccess: (success) => {
      showSuccessMessage(
        success.response?.data?.message || "Meal order updated successfully!"
      );
      queryClient.invalidateQueries(["meals"]);
    },
    onError: (error) => {
      showErrorMessage(
        error.response?.data?.message || "Failed to update meal order"
      );
      // Revert the order on error
      if (mealData?.data?.data) {
        setOrderedMeals(mealData.data.data);
      }
    },
  });

  // Add sort mutation (same as states)
  const sortMutation = useMutation({
    mutationFn: mealService.sort,
    onSuccess: (success) => {
      showSuccessMessage(
        success.response?.data?.message || "Meals reordered successfully!"
      );
      queryClient.invalidateQueries(["meals"]);
    },
    onError: (error) => {
      showErrorMessage(
        error.response?.data?.message || "Failed to reorder meals"
      );
      setShowError(true);
    },
  });

  // Helper functions
  const resetForm = () => {
    setMealName("");
    setIconImage(null);
    setErrors({
      name: "",
    });
  };

  const validateForm = () => {
    let tempErrors = {
      name: "",
    };
    let isValid = true;

    if (!mealName.trim()) {
      tempErrors.name = "Meal name is required";
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const showSuccessMessage = (message) => {
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setSuccessMessage("");
    }, 3000);
  };

  const showErrorMessage = (errorMsg) => {
    setShowError(true);
    setErrorMessage(errorMsg);
    setTimeout(() => {
      setShowError(false);
      setErrorMessage("");
    }, 3000);
  };

  // Action handlers
  const handleAddClick = () => {
    resetForm();
    setMealStatus(1);
    setAddDialogOpen(true);
  };

  const handleDeleteClick = (meal) => {
    setSelectedMeal(meal);
    setDeleteDialogOpen(true);
  };

  const handleEditClick = (mealId) => {
    getByIdMutation.mutate(mealId);
  };

  const handleSaveMeal = () => {
    setIsLoading(true);
    if (validateForm()) {
      const formData = new FormData();
      formData.append("name", mealName);
      formData.append("status", mealStatus);
      if (iconImage) {
        formData.append("icon", iconImage);
      }

      createMutation.mutate(formData);
    } else {
      setIsLoading(false);
    }
  };

  const handleUpdateMeal = () => {
    setIsLoading(true);
    if (validateForm()) {
      const formData = new FormData();
      formData.append("name", mealName);
      formData.append("status", mealStatus);
      if (iconImage) {
        formData.append("icon", iconImage);
      }

      updateMutation.mutate({
        id: selectedMeal.id,
        formData,
      });
    } else {
      setIsLoading(false);
    }
  };

  const handleDeleteMeal = () => {
    setIsLoading(true);
    if (selectedMeal) {
      deleteMutation.mutate(selectedMeal.id);
    }
  };

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const moveRow = (dragIndex, hoverIndex) => {
    setDragIndex(dragIndex);
    setHoverIndex(hoverIndex);
    const draggedMeal = orderedMeals[dragIndex];
    const updatedMeals = [...orderedMeals];
    updatedMeals.splice(dragIndex, 1);
    updatedMeals.splice(hoverIndex, 0, draggedMeal);
    setOrderedMeals(updatedMeals);
  };

  const handleDragEnd = () => {
    if (dragIndex !== null && hoverIndex !== null) {
      const sort_from = orderedMeals[dragIndex]?.id;
      const sort_to = orderedMeals[hoverIndex]?.id;
      if (sort_from && sort_to) {
        sortMutation.mutate({ sort_from, sort_to });
      }
    }
    setDragIndex(null);
    setHoverIndex(null);
  };

  const pagination = {
    currentPage: mealData?.current_page || 1,
    lastPage: mealData?.last_page || 1,
    perPage: mealData?.per_page || 10,
    total: mealData?.total || 0,
    from: mealData?.from || 0,
    to: mealData?.to || 0,
  };

  return (
    <AdminPageLayout>
      {/* Error Toster */}
      {showError && (
        <Toster
          message={errorMessage}
          type="error"
          duration={3000}
          onClose={() => setShowError(false)}
        />
      )}

      {/* Success Toster */}
      {showSuccess && successMessage && (
        <Toster
          message={successMessage}
          type="success"
          duration={3000}
          onClose={() => {
            setShowSuccess(false);
            setSuccessMessage("");
          }}
        />
      )}

      <AdminPageHeader
        title={ADMIN_PAGE_META.meals.title}
        subtitle={ADMIN_PAGE_META.meals.subtitle}
      />

      <AdminPageCard>
        <AdminPageCardToolbar>
          <AdminTableToolbar className="mb-0">
            <TableSearchInput
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search meals..."
            />
            <button
              onClick={handleAddClick}
              className="bg-primary text-white px-4 py-1 rounded-lg border border-primary shadow-md hover:bg-primary/90 transition-all"
            >
              Add Meal
            </button>
          </AdminTableToolbar>
        </AdminPageCardToolbar>

        <AdminTable
          className="shadow-none rounded-none"
          footer={
            <AdminTableFooter>
              <Pagination
                pagination={pagination}
                onPageChange={handlePageChange}
                showInfo={true}
              />
            </AdminTableFooter>
          }
        >
        <DndProvider backend={HTML5Backend}>
          <AdminTableHead>
            <AdminTableTh>#</AdminTableTh>
            <AdminTableTh>Icon</AdminTableTh>
            <AdminTableTh>Name</AdminTableTh>
            <AdminTableTh>Status</AdminTableTh>
            <AdminTableTh align="right">Actions</AdminTableTh>
          </AdminTableHead>
          <AdminTableBody>
            {orderedMeals.length > 0 ? (
              orderedMeals.map((meal, index) => (
                <SortableRow
                  key={meal.id}
                  meal={meal}
                  index={index}
                  moveRow={moveRow}
                  handleEditClick={handleEditClick}
                  handleDeleteClick={handleDeleteClick}
                  handleDragEnd={handleDragEnd}
                />
              ))
            ) : (
              <TableEmptyRow colSpan={5} emptyMessage="No meals found" />
            )}
          </AdminTableBody>
        </DndProvider>
      </AdminTable>
      </AdminPageCard>

      {/* Add Meal Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => {
          setAddDialogOpen(false);
          setErrors({ name: "" });
        }}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-bs-add"></i>
            Create Meal
          </div>
        }
        primaryButtonText="Save"
        primaryButtonAction={handleSaveMeal}
        closeButtonText="Close"
        closeButtonAction={() => {
          setAddDialogOpen(false);
          setErrors({ name: "" });
        }}
        isLoading={isLoading}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Meal Name
            </label>
            <input
              type="text"
              className={`text-sm w-full px-3 py-1 border ${
                errors.name ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary`}
              placeholder="Enter meal name"
              value={mealName}
              onChange={(e) => {
                setMealName(e.target.value);
                setErrors({ ...errors, name: "" });
              }}
            />
            {errors.name && (
              <p className="text-red-500 text-xs mt-1">{errors.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Icon Image
            </label>
            <input
              type="file"
              accept="image/*"
              className="text-sm w-full px-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              onChange={(e) => setIconImage(e.target.files[0])}
            />
            {iconImage && (
              <div className="mt-2">
                <FilePreview
                  file={iconImage}
                  label="Icon Image"
                  onDelete={() => setIconImage(null)}
                  isImage={true}
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              className="text-sm w-full px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              value={mealStatus}
              onChange={(e) => setMealStatus(Number(e.target.value))}
            >
              <option value={1}>Active</option>
              <option value={0}>In-Active</option>
            </select>
          </div>
        </div>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-rr-pencil text-[16px]"></i>
            Edit Meal
          </div>
        }
        primaryButtonText="Update"
        width="w-[500px]"
        primaryButtonAction={handleUpdateMeal}
        closeButtonText="Cancel"
        closeButtonAction={() => setEditDialogOpen(false)}
        isLoading={isLoading}
      >
        <div className="grid grid-cols-2 gap-2">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Meal Name
            </label>
            <input
              type="text"
              className="text-sm w-full px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              placeholder="Enter meal name"
              value={mealName}
              onChange={(e) => setMealName(e.target.value)}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              className="text-sm w-full px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              value={mealStatus}
              onChange={(e) => setMealStatus(Number(e.target.value))}
            >
              <option value={1}>Active</option>
              <option value={0}>In-Active</option>
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Icon Image
            </label>
            <input
              type="file"
              accept="image/*"
              className="text-sm w-full px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              onChange={(e) => setIconImage(e.target.files[0])}
            />
          </div>
        </div>

        {/* Preview Section */}
        <div className="mt-4">
          <label className="block font-bold text-sm text-gray-700 mb-1">
            Icon preview
          </label>
          {iconImage ? (
            <FilePreview
              file={iconImage}
              label="New Icon Image"
              onDelete={() => setIconImage(null)}
              isImage={true}
            />
          ) : selectedMeal?.icon && selectedMeal?.icon_url ? (
            <FilePreview
              file={selectedMeal.icon_url}
              label="Current Icon Image"
              isImage={true}
              showDelete={true}
            />
          ) : (
            <div className="text-sm text-gray-500">No icon image selected</div>
          )}
        </div>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-rr-trash text-[16px]"></i>
            Delete Meal
          </div>
        }
        primaryButtonText="Delete"
        primaryButtonAction={handleDeleteMeal}
        closeButtonText="Cancel"
        closeButtonAction={() => setDeleteDialogOpen(false)}
        isLoading={isLoading}
      >
        <div className="mt-2">
          <p className="text-sm text-gray-500">
            Are you sure you want to delete this meal? This action cannot be
            undone.
          </p>
        </div>
      </Dialog>

      {/* Loading Indicator */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-lg shadow-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </div>
      )}
    </AdminPageLayout>
  );
};

export default Meals;
